// The radial order menu's open/hover state, and the controller the map gesture layer drives it through
// (src/map/gestures.ts). Kept as its own tiny store so the menu re-renders on hover without the live
// stage re-rendering, and so the gesture controller never imports React.

import { create } from 'zustand';
import { card } from '../../sim/deck';
import type { CardDef, CardId } from '../../sim/types';
import { useStore } from '../../state/store';
import { cardReason } from '../cards/legality';
import { RADIAL_ARM, RADIAL_LABEL, radialWedges, wedgeIndexAt, type FamilyShape } from './layout';

export interface RadialWedge {
  card: CardDef;
  label: string;
  start: number;
  end: number;
  mid: number;
  color: string;
  shape: FamilyShape;
  /** Set when the card is illegal for this unit right now: the wedge is greyed and says why on hover. */
  reason?: string;
}

interface RadialState {
  open: boolean;
  unitId: number | null;
  callsign: string;
  /** Centre of the menu in the map container's CSS pixels. Usually the unit's own symbol, pulled in
   * off the edge when the unit is too close to one for the whole menu to fit. */
  center: { x: number; y: number } | null;
  /** The unit's symbol itself. When it is not the centre, a leader line joins the two. */
  anchor: { x: number; y: number } | null;
  /** 1 for a menu with room; less when it had to be reflowed smaller to fit between the rails. */
  scale: number;
  /** True once the pointer has travelled `RADIAL_ARM` from where it opened the menu. */
  armed: boolean;
  wedges: RadialWedge[];
  hover: CardId | null;
  /**
   * The press was released inside the dead zone straight away, so the menu stays up and waits for a
   * click instead of closing (Addendum A describes press-drag-release; a plain right-click that lets go
   * without moving is the same gesture with no movement, and should not dismiss the menu it just opened).
   */
  sticky: boolean;
}

export const useRadial = create<RadialState>(() => ({
  open: false,
  unitId: null,
  callsign: '',
  center: null,
  anchor: null,
  scale: 1,
  armed: false,
  wedges: [],
  hover: null,
  sticky: false,
}));

/** What `src/map/gestures.ts` is handed, so the gesture layer stays free of React and of the wedge math. */
export interface RadialController {
  open(
    unitId: number,
    center: { x: number; y: number },
    anchor: { x: number; y: number },
    scale?: number,
  ): boolean;
  close(): void;
  isOpen(): boolean;
  isSticky(): boolean;
  setSticky(sticky: boolean): void;
  /**
   * Highlights the wedge the pointer is over, if the gesture is armed.
   *
   * `dx`/`dy` are the offset from the menu's own centre, which is what decides *which* wedge; `movedPx`
   * is how far the pointer has travelled from where the menu was opened, which is what decides whether
   * any wedge at all may light. They are different points whenever the menu had to be reflowed off the
   * unit, and conflating them is what made a right-click with no movement issue an order.
   */
  track(dx: number, dy: number, movedPx: number): { card: CardId; legal: boolean } | null;
  /** Plays a wedge: a card that needs no target is issued, anything else enters targeting. */
  choose(cardId: CardId): void;
}

export const radialController: RadialController = {
  open(unitId, center, anchor, scale = 1) {
    const s = useStore.getState();
    const unit = s.snapshot?.units.find((u) => u.id === unitId);
    if (!unit || !s.grid || !s.scenario || !s.snapshot) return false;
    const graphics = s.scenario.graphics;
    const units = s.snapshot.units;
    const wedges: RadialWedge[] = radialWedges().map((w) => ({
      card: w.card,
      label: RADIAL_LABEL[w.card.id],
      start: w.start,
      end: w.end,
      mid: w.mid,
      color: w.color,
      shape: w.shape,
      reason: cardReason(w.card, unit, s.grid!, graphics, units),
    }));
    useRadial.setState({
      open: true, unitId, callsign: unit.callsign, center, anchor, scale, wedges,
      hover: null, sticky: false, armed: false,
    });
    return true;
  },

  close() {
    if (!useRadial.getState().open) return;
    useRadial.setState({
      open: false, unitId: null, center: null, anchor: null, scale: 1,
      wedges: [], hover: null, sticky: false, armed: false,
    });
  },

  isOpen: () => useRadial.getState().open,
  isSticky: () => useRadial.getState().sticky,
  setSticky: (sticky) => useRadial.setState({ sticky }),

  track(dx, dy, movedPx) {
    const s = useRadial.getState();
    if (!s.open) return null;
    // Two gates, and both have to be open: the pointer has to have left where it started (arming), and
    // it has to be out of the hub's dead zone (which wedge). A sticky menu is already armed by the
    // click that is about to land on a wedge.
    const armed = s.armed || s.sticky || movedPx > RADIAL_ARM;
    if (armed !== s.armed && !s.sticky) useRadial.setState({ armed });
    if (!armed || Math.hypot(dx, dy) <= RADIAL_ARM) {
      if (s.hover !== null) useRadial.setState({ hover: null });
      return null;
    }
    const wedge = s.wedges[wedgeIndexAt(dx, dy)];
    if (!wedge) return null;
    if (s.hover !== wedge.card.id) useRadial.setState({ hover: wedge.card.id });
    return { card: wedge.card.id, legal: wedge.reason === undefined };
  },

  choose(cardId) {
    const { unitId, wedges, armed, sticky } = useRadial.getState();
    if (unitId === null) return;
    // Nothing is chooseable until the gesture armed itself. A sticky menu is waiting for a deliberate
    // click, which is its own act of arming.
    if (!armed && !sticky) return;
    const wedge = wedges.find((w) => w.card.id === cardId);
    if (wedge?.reason) return; // greyed: the reason is already on the wedge
    const def = card(cardId);
    radialController.close();
    const s = useStore.getState();
    // Cards with nothing to aim at (cancel / stand down) go straight onto the command net; everything
    // else hands over to the stage's existing targeting mode - crosshair, preview, banner, commit.
    if (def.targetKind === 'none') s.issue(unitId, cardId, { kind: 'none' });
    else s.startTargeting(cardId);
  },
};
